import { useCallback, createRef, useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { askLizzy, setChatMessage, setIsExpanded } from 'redux/toolbar';
import sanitizeHtml from 'sanitize-html';
import classNames from 'classnames';
import ContentEditable from 'react-contenteditable';
import Icon from 'components/Icon';

const ToolbarChat = () => {
  /* State */
  const [focused, setFocused] = useState(false);
  const [blured, setBlured] = useState(false);

  /* Refs */
  const textRef = createRef();

  /* Helpers */
  const dispatch = useDispatch();

  /* Selectors */
  const isExpanded = useSelector((state) => state.toolbar.isExpanded);
  const chatMessage = useSelector((state) => state.toolbar.chatMessage);
  const selectedText = useSelector((state) => state.toolbar.selectedText);

  /* Functions */
  const sendMessage = useCallback(() => {
    dispatch(askLizzy(chatMessage, selectedText));
  }, [chatMessage, selectedText, dispatch]);

  const onContentChange = useCallback((event) => {
		const sanitizeConf = {
			allowedTags: [],
      allowedAttributes: {}
		};
    
    const html = sanitizeHtml(event.currentTarget.innerHTML, sanitizeConf);
    const hasContent = html.length > 0;

    !isExpanded && dispatch(setIsExpanded(hasContent));
    dispatch(setChatMessage(html));
	}, [dispatch, isExpanded])

  const onBlur = useCallback((event) => {
    onContentChange(event);
    setFocused(false);
    setBlured(true);
  }, [onContentChange]);

  const onFocus = (event) => {
    setFocused(true);
  };

  const keypressHandler = useCallback((event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      sendMessage();
    }
  }, [sendMessage]);

  /* Effects */
  useEffect(() => {
    const textElement = textRef.current?.el.current;
    textElement.addEventListener('keypress', keypressHandler);

    return () => {
      textElement.removeEventListener('keypress', keypressHandler);
    }
  }, [keypressHandler, sendMessage, textRef]);

  useEffect(() => {
    if (!blured) {
      setTimeout(() => {
        textRef.current?.el.current.focus();
      }, 100);
    }
  }, [textRef, blured]);

  return (
    <Wrapper>
      <ColumnWrapper logo>
        <LogoIcon
          active={focused}
          source='lizzy'
          name='lizzy-logo-plain'
        />
      </ColumnWrapper>

      <ColumnWrapper text isExpanded={isExpanded}>
        <ContentEditable
          ref={textRef}
          data-placeholder='Ask Lizzy to generate something...'
          className={classNames({
            empty: !chatMessage.length
          })}
          onChange={onContentChange}
          onFocus={onFocus}
          onBlur={onBlur}
          html={chatMessage}
        />
      </ColumnWrapper>
      <ColumnWrapper send>
        <SendButton onClick={sendMessage} active={!!chatMessage.length}>
          <Icon source='lizzy' name='lizzy-send-chat' />
        </SendButton>
      </ColumnWrapper>
    </Wrapper>
  );
}

export default ToolbarChat;

const Wrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  font-family: 'Inter';
  font-size: 13px;
  color: #ABABAC;
  background: #F3F3F3;
  border-radius: 8px;
  padding: 4px 8px;
`;

const SendButton = styled.button`
  all: unset;
  cursor: pointer;
  align-self: end;
  width: 16px;
  height: 16px;

  ${p => p.active && css`
    ${Icon} {
      color: var(--lizzy-blue);
    }
  `}
`;

const ColumnWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: start;
  justify-content: center;

  ${p => p.logo && css`
    padding-top: 4px;
    padding-right: 0px;
  `}

  ${p => p.text && css`
    padding: 0 5px;
    width: calc(100% - 36px);
  `}

  & > [contenteditable="true"] {
    width: 100%;
    color: #000;
    min-height: 23px;

    &:focus-visible {
      outline: 0;
    }

    &.empty {
      &::before {
        position: absolute;
        content: attr(data-placeholder);
        color: var(--lizzy-black-35);
      }
    }
  }

  .lizzy-send {
    width: 17px;
    height: 17px;
    fill: var(--lizzy-black-35);
  }

  ${SendButton} {
    margin-bottom: 3px;
  }
`;

const LogoIcon = styled(Icon)`
  width: 16px;
  height: 16px;

  ${p => p.active && css`
    color: var(--lizzy-blue-65);
  `}
`;